import type { ChangeEvent, FC } from "react";

import { TabList, TabPanel, Tabs } from "@similarweb/ui-components";

import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { translate } from "app/filters/i18nFilter";

import { KeywordsSection, KeywordsGroupSection } from "../components/AutocompleteKeywordGroupsComponents";
import { StyledTab } from "../styles/AutocompleteKeywordStyles";
import type { IKeywordAutocompleteResults, ISearchedKeyword } from "../types/AutocompleteKeywordGroupTypes";

interface RenderAutocompleteContentProps {
    data: {
        listItems: IKeywordAutocompleteResults;
        selectedItemId: number;
    };
    selectedIndex: number;
    onTabSelected: (index: number) => void;
    renderItemsClick: (item: unknown, isKwGroup?: boolean) => (e: ChangeEvent<HTMLInputElement> | Event) => void;
    handleGroupEditClick: (item: IUserKeywordGroup) => void;
    handleGroupShareClick: (item: IUserKeywordGroup) => void;
    handleAutoCreateKeywordGroupClick: (item: ISearchedKeyword) => void;
    showKeywords: boolean;
    isAutocompleteLoading: boolean;
    hasQuery: boolean;
    tabTitles: {
        keywordsTabTitle?: string;
        keywordGroupsTabTitle?: string;
    };
    showEditButton: boolean;
    showShareButton: boolean;
    showAutoCreateKeywordGroupButton: boolean;
}

export const RenderAutocompleteContent: FC<RenderAutocompleteContentProps> = ({
    data: { listItems, selectedItemId },
    selectedIndex,
    onTabSelected,
    renderItemsClick,
    handleGroupEditClick,
    handleGroupShareClick,
    handleAutoCreateKeywordGroupClick,
    showKeywords,
    isAutocompleteLoading,
    hasQuery,
    tabTitles,
    showEditButton,
    showShareButton,
    showAutoCreateKeywordGroupButton,
}) => {
    const { keywords, keywordGroups, sharedKeywordGroups, topics } = listItems;
    const hasAnyData =
        (keywords?.length ?? 0) > 0 ||
        keywordGroups?.length > 0 ||
        sharedKeywordGroups?.length > 0 ||
        (topics?.length ?? 0) > 0;
    const hideResultsCount = isAutocompleteLoading || !hasQuery;

    if (!hasAnyData) {
        return null;
    }

    const renderTabContent = () => (
        <>
            <TabPanel>
                <KeywordsSection
                    keywords={keywords}
                    selectedItemId={selectedItemId}
                    isRecentSearches={!hasQuery}
                    showAutoCreateKeywordGroupButton={showAutoCreateKeywordGroupButton}
                    onAutoCreateKeywordGroupClick={handleAutoCreateKeywordGroupClick}
                    translate={translate}
                    renderOnItemClick={(item) => renderItemsClick(item, false)}
                />
            </TabPanel>

            <TabPanel>
                <KeywordsGroupSection
                    keywordGroups={keywordGroups}
                    sharedKeywordGroups={sharedKeywordGroups}
                    selectedItemId={selectedItemId}
                    translate={translate}
                    renderOnItemClick={(item) => renderItemsClick(item, false)}
                    onEditClick={handleGroupEditClick}
                    onShareClick={handleGroupShareClick}
                    showEditButton={showEditButton}
                    showShareButton={showShareButton}
                />
            </TabPanel>
        </>
    );

    // @ts-ignore FIXME: strict migration - Argument of type 'string | undefined' is not assignable to parameter of type 'string | string[]' — tabTitles fields are optional in props but callers always supply both titles (defaults set in AutocompleteKeywordGroups)
    const keywordsTabTitle = translate(tabTitles.keywordsTabTitle);
    // @ts-ignore FIXME: strict migration - Argument of type 'string | undefined' is not assignable to parameter of type 'string | string[]' — tabTitles fields are optional in props but callers always supply both titles (defaults set in AutocompleteKeywordGroups)
    const keywordGroupsTabTitle = translate(tabTitles.keywordGroupsTabTitle);

    return showKeywords ? (
        <div className="ListItemsContainer">
            <Tabs
                selectedIndex={selectedIndex}
                onSelect={onTabSelected}
            >
                <TabList>
                    <StyledTab>
                        {keywordsTabTitle}
                        {!hideResultsCount ? ` (${keywords?.length ?? 0})` : null}
                    </StyledTab>
                    <StyledTab>
                        {keywordGroupsTabTitle}
                        {!hideResultsCount ? ` (${keywordGroups.length + sharedKeywordGroups.length})` : null}
                    </StyledTab>
                </TabList>
                {renderTabContent()}
            </Tabs>
        </div>
    ) : (
        <div>
            <KeywordsGroupSection
                keywordGroups={keywordGroups}
                sharedKeywordGroups={sharedKeywordGroups}
                selectedItemId={selectedItemId}
                translate={translate}
                renderOnItemClick={renderItemsClick}
                onEditClick={handleGroupEditClick}
                onShareClick={handleGroupShareClick}
                showEditButton={showEditButton}
                showShareButton={showShareButton}
            />
        </div>
    );
};
